import { useMemo, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { Button } from '../../Common/Button'
import { OwnerFirmConfirm } from '../kit/OwnerConfirm'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import {
  setMobileChannelRelease,
  type MobileChannelPointer,
  type MobileRelease,
  type MobileSurface,
} from '../../../services/ownerDemoLaneService'
import { useMobileReleases } from './useMobileReleases'
import './ownerDemoLane.css'

const SURFACES: MobileSurface[] = ['member', 'helper']

const shortSha = (sha: string | null): string => (sha ? sha.slice(0, 7) : '—')
const shortDate = (iso: string | null): string => (iso ? iso.slice(0, 16).replace('T', ' ') : '—')
const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error'

type Pending = { surface: MobileSurface; release: MobileRelease; older: boolean }

/**
 * Phone apps section of the Demo lane (R35 DL4.c): what the SIT and demo channels point at, the last 10 releases
 * per app, Promote to demo / Roll back. The UI only sets the demo channel (the server also allows SIT).
 */
export function DemoPhoneAppsSection() {
  const { t } = useNls()
  const { data, loading, error, reload } = useMobileReleases(true)
  const [target, setTarget] = useState<Pending | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const surfaceName = (surface: MobileSurface): string =>
    surface === 'member' ? t('NLSOwnerConsoleDemoLanePhoneMember') : t('NLSOwnerConsoleDemoLanePhoneHelper')

  const confirmPromote = async () => {
    if (!target) return
    const { surface, release } = target
    setBusy(true)
    setActionError(null)
    try {
      await setMobileChannelRelease({ surface, channel: 'demo', version: release.version })
      setDone(t('NLSOwnerConsoleDemoLanePhoneDone', { app: surfaceName(surface), version: release.version }))
      setTarget(null)
      await reload()
    } catch (err) {
      setActionError(messageOf(err))
      setTarget(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="ownerDemoLaneSection" aria-label={t('NLSOwnerConsoleDemoLanePhoneTitle')}>
      <div className="ownerPanelHeader">
        <div>
          <h3>{t('NLSOwnerConsoleDemoLanePhoneTitle')}</h3>
          <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLanePhoneHint')}</p>
        </div>
        <button type="button" className="ownerModalBtn ownerModalBtn--default" onClick={() => void reload()} disabled={loading || busy}>
          {t('NLSOwnerConsoleDemoLaneRefresh')}
        </button>
      </div>
      <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLanePhoneNativeNote')}</p>

      {loading && !data ? <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLaneLoading')}</p> : null}
      {error ? (
        <div role="alert">
          <strong>{t('NLSOwnerConsoleDemoLanePhoneLoadFailed')}</strong>
          <p className="ownerDemoLaneError">{error}</p>
        </div>
      ) : null}
      {done ? <p className="ownerDemoLaneNote" role="status">{done}</p> : null}
      {actionError ? (
        <div role="alert">
          <strong>{t('NLSOwnerConsoleDemoLanePhonePromoteFailed')}</strong>
          <p className="ownerDemoLaneError">{actionError}</p>
        </div>
      ) : null}

      {data ? SURFACES.map((surface) => (
        <PhoneSurfaceBlock
          key={surface}
          surface={surface}
          name={surfaceName(surface)}
          releases={data.releases.filter((release) => release.surface === surface)}
          sit={data.pointers[surface].sit}
          demo={data.pointers[surface].demo}
          busy={busy}
          onPick={(pending) => { setActionError(null); setDone(null); setTarget(pending) }}
        />
      )) : null}

      <OwnerFirmConfirm
        isOpen={target !== null}
        title={target?.older ? t('NLSOwnerConsoleDemoLanePhoneRollBackTitle') : t('NLSOwnerConsoleDemoLanePhonePromoteTitle')}
        description={target ? t('NLSOwnerConsoleDemoLanePhoneConfirmDescription', { app: surfaceName(target.surface), version: target.release.version }) : undefined}
        confirmLabel={target?.older ? t('NLSOwnerConsoleDemoLanePhoneRollBack') : t('NLSOwnerConsoleDemoLanePhonePromote')}
        busy={busy}
        onClose={() => setTarget(null)}
        onConfirm={confirmPromote}
      />
    </section>
  )
}

function PhoneSurfaceBlock(props: {
  surface: MobileSurface
  name: string
  releases: MobileRelease[]
  sit: MobileChannelPointer
  demo: MobileChannelPointer
  busy: boolean
  onPick: (pending: Pending) => void
}) {
  const { surface, name, releases, sit, demo, busy, onPick } = props
  const { t } = useNls()
  const [copiedApk, setCopiedApk] = useState(false)

  const handleCopyApk = () => {
    if (!demo.currentApkUrl) return
    navigator.clipboard?.writeText(demo.currentApkUrl)
    setCopiedApk(true)
    setTimeout(() => setCopiedApk(false), 2000)
  }

  // Releases arrive newest first, so a release listed after the demo channel's current one is older.
  const currentIndex = demo.version ? releases.findIndex((release) => release.version === demo.version) : -1
  const pointerText = (pointer: MobileChannelPointer): string =>
    pointer.version ? `${pointer.version} · ${shortDate(pointer.releasedAt)}` : t('NLSOwnerConsoleDemoLanePhoneNoRelease')

  const columns = useMemo<ColumnDef<MobileRelease>[]>(() => [
    { key: 'version', header: t('NLSOwnerConsoleDemoLanePhoneColVersion'), accessor: (r) => r.version },
    { key: 'channel', header: t('NLSOwnerConsoleDemoLanePhoneColChannel'), accessor: (r) => r.channel ?? '—' },
    { key: 'sha', header: t('NLSOwnerConsoleDemoLaneColCommit'), accessor: (r) => shortSha(r.gitSha), render: (r) => <code>{shortSha(r.gitSha)}</code> },
    { key: 'date', header: t('NLSOwnerConsoleDemoLanePhoneColDate'), accessor: (r) => shortDate(r.createdAt) },
    {
      key: 'status',
      header: t('NLSOwnerConsoleDemoLaneColStatus'),
      render: (r) => {
        if (r.version === demo.version) return <span className="ownerDemoLaneBadge">{t('NLSOwnerConsoleDemoLanePhoneOnDemo')}</span>
        const index = releases.indexOf(r)
        const older = currentIndex >= 0 && index > currentIndex
        const label = older ? t('NLSOwnerConsoleDemoLanePhoneRollBack') : t('NLSOwnerConsoleDemoLanePhonePromote')
        return (
          <button
            type="button"
            className="ownerModalBtn ownerModalBtn--default"
            disabled={busy}
            onClick={() => onPick({ surface, release: r, older })}
            aria-label={`${label} ${name} ${r.version}`}
          >
            {label}
          </button>
        )
      },
    },
  ], [t, demo.version, releases, currentIndex, busy, surface, name, onPick])

  return (
    <div className="ownerDemoLaneSurface" role="group" aria-label={name}>
      <strong>{name}</strong>
      <div className="ownerDemoLaneLive">
        <span>{t('NLSOwnerConsoleDemoLanePhoneSitChannel')}: <code>{pointerText(sit)}</code></span>
        <span>{t('NLSOwnerConsoleDemoLanePhoneDemoChannel')}: <code>{pointerText(demo)}</code></span>
      </div>
      {demo.currentApkUrl ? (
        <>
          <div className="ownerDemoLaneApk">
            <span>{t('NLSOwnerConsoleDemoLanePhoneApkLabel')}: <a href={demo.currentApkUrl} target="_blank" rel="noopener noreferrer">{demo.currentApkUrl}</a></span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleCopyApk}
              disabled={busy}
            >
              {copiedApk ? t('NLSOwnerConsoleDemoLanePhoneApkCopied') : t('NLSOwnerConsoleDemoLanePhoneApkCopy')}
            </Button>
          </div>
          <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLanePhoneApkNote')}</p>
        </>
      ) : (
        <p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLanePhoneApkNone')}</p>
      )}
      <OwnerTable<MobileRelease>
        data={releases}
        columns={columns}
        viewKey={`demo-lane-phone-${surface}`}
        getRowId={(r) => r.releaseId}
        emptyState={<p className="ownerDemoLaneHint">{t('NLSOwnerConsoleDemoLanePhoneEmpty')}</p>}
      />
    </div>
  )
}
